import editorConfig from '~config/editorConfig';
import { ALIGNMENT_TYPES, DEFAULT_ALIGNMENT_CONFIG } from './constants';
import { calculateRelativeThresholds } from '../snapping/calculateRelativeThresholds';
import { drawDistanceText } from './visualizers/alignmentLineRenderer';

/**
 * AlignmentManager - Handles visual alignment guides without affecting object positions
 * Works in conjunction with SnapManager for complete positioning experience
 */
export class AlignmentManager {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getSelectionContext();

    // Get base settings
    const baseSettings = {
      ...DEFAULT_ALIGNMENT_CONFIG,
      ...editorConfig.alignmentSettings,
      ...options,
    };

    // Store original options for recalculation
    this._originalOptions = options;

    // Calculate relative thresholds based on canvas size
    this.options = calculateRelativeThresholds(canvas, baseSettings);

    // State
    this.alignmentLines = [];
    this.activeStrategies = new Map();
    this.cleanup = [];
    this.viewportTransform = null;
    this.zoom = 1;

    this._initializeEventHandlers();
  }

  /**
   * Register an alignment strategy
   * @param {string} type - The alignment type
   * @param {Object} strategy - The strategy implementation
   */
  registerStrategy(type, strategy) {
    if (!strategy.checkAlignment || !strategy.getAlignmentLines) {
      throw new Error(
        'Alignment strategy must implement checkAlignment and getAlignmentLines methods'
      );
    }
    this.activeStrategies.set(type, strategy);
  }

  /**
   * Unregister an alignment strategy
   * @param {string} type - The alignment type to remove
   */
  unregisterStrategy(type) {
    this.activeStrategies.delete(type);
  }

  _initializeEventHandlers() {
    // Update viewport on zoom/pan
    const updateViewport = () => {
      this.viewportTransform = this.canvas.viewportTransform;
      this.zoom = this.canvas.getZoom();
    };

    this.canvas.on('canvas:viewportTransformed', updateViewport);
    this.cleanup.push(() =>
      this.canvas.off('canvas:viewportTransformed', updateViewport)
    );

    // Initialize viewport
    updateViewport();

    // Handle object moving
    const handleObjectMoving = (e) => {
      this._handleObjectMoving(e);
    };

    this.canvas.on('object:moving', handleObjectMoving);
    this.cleanup.push(() =>
      this.canvas.off('object:moving', handleObjectMoving)
    );

    // Handle object scaling
    const handleObjectScaling = (e) => {
      this._handleObjectScaling(e);
    };

    this.canvas.on('object:scaling', handleObjectScaling);
    this.cleanup.push(() =>
      this.canvas.off('object:scaling', handleObjectScaling)
    );

    // Clear alignment lines when done moving or scaling
    const handleObjectModified = () => {
      this.clearAlignmentLines();
    };

    this.canvas.on('object:modified', handleObjectModified);
    this.cleanup.push(() =>
      this.canvas.off('object:modified', handleObjectModified)
    );

    // Handle rendering
    const clearContext = () => {
      this.canvas.clearContext(this.ctx);
    };

    const renderAlignmentLines = () => {
      this._renderAlignmentLines();
    };

    this.canvas.on('before:render', clearContext);
    this.canvas.on('after:render', renderAlignmentLines);

    this.cleanup.push(() => {
      this.canvas.off('before:render', clearContext);
      this.canvas.off('after:render', renderAlignmentLines);
    });
  }

  _handleObjectMoving(e) {
    const activeObject = e.target;
    if (!activeObject) return;

    // Skip guidelines - they should not trigger alignment lines
    if (activeObject.isGuideline) {
      return;
    }

    // Skip if alignment lines are disabled
    const settings = window.getState?.('settings') || {};
    if (settings.alignmentLinesEnabled === false) return;

    // Clear previous alignment lines
    this.alignmentLines = [];

    // MagicFrame objects are now supported for alignment

    // Run all registered strategies with 'moving' context
    for (const [type, strategy] of this.activeStrategies) {
      if (this._isStrategyEnabled(type)) {
        const alignmentData = strategy.checkAlignment(
          activeObject,
          this.canvas,
          this.options,
          'moving' // Pass context to indicate we're moving
        );

        if (alignmentData && alignmentData.lines) {
          this.alignmentLines.push(...alignmentData.lines);
        }
      }
    }
  }

  _handleObjectScaling(e) {
    const activeObject = e.target;
    if (!activeObject) return;

    // Skip guidelines - they should not trigger alignment lines
    if (activeObject.isGuideline) {
      return;
    }

    // Skip if alignment lines are disabled
    const settings = window.getState?.('settings') || {};
    if (settings.alignmentLinesEnabled === false) return;

    // Clear previous alignment lines
    this.alignmentLines = [];

    // MagicFrame objects are now supported for alignment

    // Run all registered strategies with scaling context
    for (const [type, strategy] of this.activeStrategies) {
      if (this._isStrategyEnabled(type)) {
        const alignmentData = strategy.checkAlignment(
          activeObject,
          this.canvas,
          this.options,
          'scaling' // Add context to indicate we're scaling
        );

        if (alignmentData && alignmentData.lines) {
          this.alignmentLines.push(...alignmentData.lines);
        }
      }
    }
  }

  _isStrategyEnabled(type) {
    // First check if alignment lines are globally disabled
    const settings = window.getState?.('settings') || {};
    if (settings.alignmentLinesEnabled === false) {
      return false;
    }

    // Use options from AlignmentManager which are updated by applySettings
    switch (type) {
      case ALIGNMENT_TYPES.OBJECT_TO_OBJECT:
        return this.options.enableObjectAlignment !== false;
      case ALIGNMENT_TYPES.CANVAS_CENTER:
        return this.options.enableCenterAlignment !== false;
      default:
        return true;
    }
  }

  _renderAlignmentLines() {
    // Skip if alignment lines are disabled
    const settings = window.getState?.('settings') || {};
    if (settings.alignmentLinesEnabled === false) return;

    if (!this.viewportTransform || this.alignmentLines.length === 0) return;

    this.ctx.save();

    try {
      // Set common line properties
      this.ctx.shadowColor = 'transparent';
      this.ctx.shadowBlur = 0;
      this.ctx.shadowOffsetX = 0;
      this.ctx.shadowOffsetY = 0;

      // Render each alignment line
      for (const line of this.alignmentLines) {
        this._drawAlignmentLine(line);
      }

      // Render distance text for lines that have distance information
      for (const line of this.alignmentLines) {
        if (line.distance || line.distance === 0) {
          drawDistanceText(
            this.ctx,
            line,
            this.viewportTransform,
            this.zoom,
            this.options
          );
        }
      }
    } finally {
      this.ctx.restore();
    }
  }

  _drawAlignmentLine(line) {
    const lineWidth = line.width || this.options.lineWidth;
    const lineColor = line.color || this.options.lineColor;

    this.ctx.lineWidth = lineWidth;
    this.ctx.strokeStyle = lineColor;
    this.ctx.beginPath();

    if (line.type === 'vertical') {
      const x = Math.round((line.x + this.viewportTransform[4]) * this.zoom);
      const y1 = Math.round((line.y1 + this.viewportTransform[5]) * this.zoom);
      const y2 = Math.round((line.y2 + this.viewportTransform[5]) * this.zoom);

      this.ctx.moveTo(x + 0.5, y1);
      this.ctx.lineTo(x + 0.5, y2);
    } else if (line.type === 'horizontal') {
      const y = Math.round((line.y + this.viewportTransform[5]) * this.zoom);
      const x1 = Math.round((line.x1 + this.viewportTransform[4]) * this.zoom);
      const x2 = Math.round((line.x2 + this.viewportTransform[4]) * this.zoom);

      this.ctx.moveTo(x1, y + 0.5);
      this.ctx.lineTo(x2, y + 0.5);
    }

    this.ctx.stroke();
  }

  /**
   * Clear all alignment lines
   */
  clearAlignmentLines() {
    this.alignmentLines = [];
    this.canvas.renderAll();
  }

  /**
   * Update alignment options
   * @param {Object} options - New options to merge
   */
  updateOptions(options) {
    // Update original options
    this._originalOptions = { ...this._originalOptions, ...options };

    // Get base settings with new options
    const baseSettings = {
      ...DEFAULT_ALIGNMENT_CONFIG,
      ...editorConfig.alignmentSettings,
      ...this._originalOptions,
    };

    // Recalculate thresholds with new options
    this.options = calculateRelativeThresholds(this.canvas, baseSettings);
  }

  /**
   * Get current alignment lines
   * @returns {Array} Current alignment lines
   */
  getAlignmentLines() {
    return [...this.alignmentLines];
  }

  /**
   * Check if alignment manager is active
   * @returns {boolean} Whether alignment is active
   */
  isActive() {
    const settings = window.getState?.('settings') || {};
    return settings.alignmentLinesEnabled !== false;
  }

  /**
   * Clean up all event listeners and resources
   */
  destroy() {
    try {
      this.cleanup.forEach((fn) => fn());
      this.cleanup = [];
      this.alignmentLines = [];
      this.activeStrategies.clear();
    } catch (error) {
      console.error('Error destroying AlignmentManager:', error);
    }
  }
}
